import { PlusIcon, XIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import {
  type Condition,
  type ConditionMatch,
  type ConditionScope,
  DAYS_AGO_OPERATORS,
  EVENT_PREDICATES,
  type EventPredicate,
  FIELD_OPERATORS,
  type FieldOperator,
  NEW_CONDITION,
  operatorNeedsValue,
} from 'app/lib/workflows/conditions'
import {
  conditionFieldsBySource,
  fieldDisplayLabel,
  isBooleanFieldKey,
  isDateFieldKey,
  isDaysAgoFieldKey,
} from 'app/lib/workflows/dataContext'
import { DateValueField } from './DateValueField'
import { ChoiceSelect, type Choice, type ChoiceGroup, Field, TextField } from './fields'

const OPERATOR_CHOICES: Choice[] = FIELD_OPERATORS.map((option) => ({ value: option.value, label: option.label }))
const DAYS_AGO_CHOICES: Choice[] = DAYS_AGO_OPERATORS.map((option) => ({ value: option.value, label: option.label }))
/** In "just changed" scope the row must read as a sentence about the NEW value — "Lead status ·
 *  just changed · to · Closed Lost" — so nobody has to wonder whether "is" means to or from
 *  (Patrick's exact question, 08-06). Same operators, change-scope wording. */
const CHANGE_OPERATOR_LABEL: Record<FieldOperator, string> = {
  is: `to`,
  is_not: `to anything but`,
  contains: `to text containing`,
  greater_than: `to above`,
  greater_equal_than: `to at least`,
  lesser_than: `to below`,
  lesser_equal_than: `to at most`,
  is_set: `to any value`,
  is_empty: `to empty (cleared)`,
  more_than_days_ago: `more than`,
  less_than_days_ago: `less than`,
}
const CHANGE_OPERATOR_CHOICES: Choice[] = FIELD_OPERATORS.map((option) => ({
  value: option.value,
  label: CHANGE_OPERATOR_LABEL[option.value],
}))
/** A boolean field is a two-way choice, never a typed value. Stored as the strings every condition
 *  value is stored as; the serializer converts per destination. */
const BOOLEAN_CHOICES: Choice[] = [
  { value: `true`, label: `Yes` },
  { value: `false`, label: `No` },
]

// Field keys and event predicates share one "subject" dropdown, prefixed to tell them apart.
function subjectValue(condition: Condition): string {
  if (condition.type === `event`) return `e:${condition.event}`
  return condition.field ? `f:${condition.field}` : ``
}

function RemoveButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <Button
      variant="subtle"
      size="icon-sm"
      onClick={onClick}
      aria-label="Remove condition"
      className={cn(`mt-1`, className)}
    >
      <XIcon size={14} weight="bold" className="size-3.5" />
    </Button>
  )
}

/** Shared condition-group editor used by the trigger's two condition groups and by Filter/Branch.
 *  Each row checks an in-scope field (field / operator / value) or an event predicate. The TRIGGER
 *  renders two of these with a `fixedScope`: which group a row lives in IS its scope (the old
 *  per-row "just changed / currently" dropdown asked users to compile semantics from two words;
 *  structure explains better than vocabulary). Steps omit fixedScope, since they always check state.
 *
 *  Rows are flat (design language rule 1): no per-row well, a hairline between rows, and the
 *  all/any choice is the "and" / "or" chip that leads every row after the first. */
export function ConditionEditor({
  match,
  conditions,
  onChange,
  availableFields,
  valueOptions,
  error,
  fixedScope,
  changeWording = fixedScope === `change`,
  label = `Conditions`,
  addLabel = `Add condition`,
  showMatch = true,
  events = fixedScope !== `state`,
}: {
  match: ConditionMatch
  conditions: Condition[]
  onChange: (patch: { match?: ConditionMatch; conditions?: Condition[] }) => void
  availableFields: string[]
  /** Per-field value pickers: a field key → its selectable values (e.g. `pipelineStage` → stage names).
   *  Fields without an entry fall back to a free-text value input. */
  valueOptions?: Record<string, readonly Choice[]>
  error?: string
  /** TRIGGER groups: every row in this editor carries this scope ('change' = the new value the event
   *  wrote; 'state' = the live record at that moment). Omitted for steps. */
  fixedScope?: ConditionScope
  /** Reads operators as the new value ("to", "to text containing"). A created event writes values
   *  without changing them, so its change group turns this off and reads "is". */
  changeWording?: boolean
  /** The Field label above the rows; null renders the rows bare (the caller owns the heading). */
  label?: string | null
  addLabel?: string
  /** Whether rows after the first carry the "and" / "or" chip that toggles all/any. */
  showMatch?: boolean
  /** Offer event predicates as subjects. Off for state groups and for steps that read only a record. */
  events?: boolean
}) {
  const copy = useListCopy()

  // Subject = an in-scope field (grouped by Contact / Activity) or an event predicate. Event
  // predicates partition into the change group (see conditionScope), so the state editor omits them.
  const subjectGroups: ChoiceGroup[] = [
    ...conditionFieldsBySource(availableFields).map((group) => ({
      label: group.label,
      choices: group.fields.map((field) => ({ value: `f:${field.key}`, label: fieldDisplayLabel(field, copy) })),
    })),
    ...(!events
      ? []
      : [
          {
            label: `Events`,
            choices: EVENT_PREDICATES.map((event) => ({ value: `e:${event.value}`, label: event.label })),
          },
        ]),
  ]

  const setConditions = (next: Condition[]) => onChange({ conditions: next })
  const replace = (index: number, condition: Condition) =>
    setConditions(conditions.map((c, i) => (i === index ? condition : c)))

  const setSubject = (index: number, value: string) => {
    if (value.startsWith(`e:`)) {
      replace(index, { type: `event`, event: value.slice(2) as EventPredicate })
    } else if (value.startsWith(`f:`)) {
      const field = value.slice(2)
      const prev = conditions[index]
      const daysAgo = isDaysAgoFieldKey(field)
      const keep = prev.type === `field` && isDaysAgoFieldKey(prev.field) === daysAgo
      replace(index, {
        type: `field`,
        field,
        operator: keep && prev.type === `field` ? prev.operator : daysAgo ? `more_than_days_ago` : `is`,
        value: keep && prev.type === `field` ? prev.value : undefined,
        ...(fixedScope ? { scope: fixedScope } : {}),
      })
    }
  }

  const rows = (
    <div className="divide-y divide-subtle">
      {conditions.map((condition, index) => (
        <div key={index} className="space-y-1.5 py-2 first:pt-0">
          {/* Every row after the first leads with the and/or chip. One click flips the whole group,
              which is the only choice there is: all rows in a group share one match. */}
          {showMatch && index > 0 && (
            <Button
              variant="white"
              size="xs"
              onClick={() => onChange({ match: match === `all` ? `any` : `all` })}
              aria-label={match === `all` ? `Match all, switch to any` : `Match any, switch to all`}
              className="h-5 rounded-full px-2 text-[10px] font-semibold tracking-wide text-subtle uppercase"
            >
              {match === `all` ? `and` : `or`}
            </Button>
          )}
          {/* Subject row carries the remove button, so the rows below get the full drawer width. */}
          <div className="flex items-start gap-1.5">
            <div className="min-w-0 flex-1">
              <ChoiceSelect
                value={subjectValue(condition)}
                groups={subjectGroups}
                placeholder={events ? `Choose a field or event…` : `Choose a field…`}
                invalid={!!error && condition.type === `field` && !condition.field}
                onChange={(v) => setSubject(index, v)}
              />
            </div>
            {/* Always offered, including on the last row. Hiding it there left a group that could be
                narrowed but never cleared: the only ways out of a single unwanted check were deleting
                the whole node or editing the doc through the API. Emptying a Branch/Filter is caught
                by `validateNode` ("Add at least one condition"), and an empty trigger group is a
                legitimate state that `emptyHint` already describes. */}
            <RemoveButton className="mt-0" onClick={() => setConditions(conditions.filter((_, i) => i !== index))} />
          </div>
          {condition.type === `field` && (
            <>
              <ChoiceSelect
                value={condition.operator}
                choices={
                  isDaysAgoFieldKey(condition.field)
                    ? DAYS_AGO_CHOICES
                    : changeWording
                      ? CHANGE_OPERATOR_CHOICES
                      : OPERATOR_CHOICES
                }
                onChange={(v) => replace(index, { ...condition, operator: v as FieldOperator })}
              />
              {/* The value breathes on its own full-width row. */}
              {operatorNeedsValue(condition.operator) &&
                (isDaysAgoFieldKey(condition.field) ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-subtle">
                      <TextField
                        type="number"
                        min={1}
                        step={1}
                        aria-label="Number of days"
                        className="w-20"
                        invalid={!!error && !condition.value}
                        value={condition.value ?? ``}
                        onChange={(e) => replace(index, { ...condition, value: e.target.value || undefined })}
                      />
                      <span>days ago</span>
                    </div>
                    <p className="text-xs text-subtle">Counts whole days: 1 day ago means before yesterday.</p>
                  </div>
                ) : isBooleanFieldKey(condition.field) ? (
                  <ChoiceSelect
                    value={condition.value ?? ``}
                    choices={BOOLEAN_CHOICES}
                    placeholder="Select…"
                    invalid={!!error && !condition.value}
                    onChange={(v) => replace(index, { ...condition, value: v || undefined })}
                  />
                ) : isDateFieldKey(condition.field) ? (
                  <DateValueField
                    value={condition.value}
                    invalid={!!error && !condition.value}
                    onChange={(v) => replace(index, { ...condition, value: v })}
                  />
                ) : condition.field && valueOptions?.[condition.field] ? (
                  <ChoiceSelect
                    value={condition.value ?? ``}
                    // A stored value outside today's vocabulary (renamed status, imported doc)
                    // must stay visible and selectable — never silently blank.
                    choices={
                      condition.value && !valueOptions[condition.field].some((c) => c.value === condition.value)
                        ? [...valueOptions[condition.field], { value: condition.value, label: condition.value }]
                        : valueOptions[condition.field]
                    }
                    placeholder="Select…"
                    invalid={!!error && !condition.value}
                    onChange={(v) => replace(index, { ...condition, value: v || undefined })}
                  />
                ) : (
                  <TextField
                    value={condition.value ?? ``}
                    placeholder="Value"
                    onChange={(e) => replace(index, { ...condition, value: e.target.value })}
                  />
                ))}
            </>
          )}
          {condition.type === `event` && (
            <div className="flex items-center gap-2 text-xs text-subtle">
              <span>within</span>
              <TextField
                type="number"
                min={1}
                className="w-20"
                value={condition.withinDays ?? ``}
                placeholder="any"
                onChange={(e) =>
                  replace(index, {
                    ...condition,
                    withinDays: e.target.value ? Math.max(1, Number(e.target.value)) : undefined,
                  })
                }
              />
              <span>days</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )

  return (
    <>
      {label === null ? (
        rows
      ) : (
        <Field label={label} error={error}>
          {rows}
        </Field>
      )}
      <div className="text-xs">
        <Button
          variant="subtle"
          size="inline"
          onClick={() =>
            setConditions([...conditions, { ...NEW_CONDITION, ...(fixedScope ? { scope: fixedScope } : {}) }])
          }
        >
          <PlusIcon size={13} weight="bold" className="size-[13px]" /> {addLabel}
        </Button>
      </div>
    </>
  )
}
